import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatMessageCard } from "../../ui/ChatMessageCard";
import { ChatOptions, type ChatOptionsFreeText } from "../../ui/ChatOptions";
import { RedactedQuestion } from "./RedactedQuestion";

/**
 * The assistant's question to the reader, inside its message. An option
 * answers at once; the "Other…" field takes an answer in the reader's own
 * words, and Send (or Enter) answers with it, the same way. Once answered,
 * the answer stays as the reader's chat bubble. The desktop keeps the question
 * above it, in the answer's prose, so the conversation reads as it went.
 */
export function AskUserCard({
  question,
  options,
  answer,
  onAnswer,
}: {
  question: string;
  options: ReadonlyArray<string>;
  answer: string | null;
  // Null where the reader cannot answer: a shared conversation.
  onAnswer: ((text: string) => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  // The text typed in the "Other…" field: UI state of this card alone.
  const [draft, setDraft] = useState("");
  if (answer !== null) {
    if (!isDesktopShell())
      return (
        <ChatMessageCard variant="default">
          <RedactedQuestion text={answer} />
        </ChatMessageCard>
      );
    return (
      <div className="flex flex-col gap-3">
        <p className="m-0 font-dm-sans text-gpt-answer text-ds-slate-700">
          {question}
        </p>
        <ChatMessageCard variant="desktop">
          <RedactedQuestion text={answer} />
        </ChatMessageCard>
      </div>
    );
  }
  const text = draft.trim();
  // The "Other…" field and the Send that answers with it: ChatOptions types
  // them as a pair, and neither exists where the reader cannot answer.
  const typedAnswer =
    onAnswer === null
      ? { freeText: null, onContinue: null }
      : {
          freeText: {
            kind: "field",
            label: t("Other…"),
            placeholder: t("Your answer…"),
            value: draft,
            onChange: (value) => {
              setDraft(value);
            },
          } satisfies ChatOptionsFreeText,
          onContinue: () => {
            onAnswer(text);
            setDraft("");
          },
        };
  return (
    <ChatOptions
      title={question}
      select="single"
      options={options.map((label, index) => ({ id: String(index), label }))}
      selectedIds={[]}
      onSelect={
        onAnswer === null
          ? null
          : (id) => {
              const label = options[Number(id)];
              if (label === undefined)
                throw new Error(`should not happen: option ${id} missing`);
              onAnswer(label);
            }
      }
      {...typedAnswer}
      continueLabel={t("Send")}
      continueDisabled={text === ""}
      onClose={null}
      pager={null}
      placement="transcript"
    />
  );
}
